/**
 * Device State Parser - Trait-Based State Extraction
 * 
 * This module provides unified state extraction based on device traits,
 * eliminating the need for device-type-specific switch statements.
 * 
 * Each trait maps to specific state properties that are extracted from
 * various data formats (API response, WebSocket delta, etc.)
 * 
 * ============================================================================
 * TRAIT MAPPING (Current)
 * ============================================================================
 * ├── OnOff          → isOn, switchOn, power
 * ├── Brightness     → brightness (0-100)
 * ├── ColorSetting   → colorTemp (2700-6500K), spectrumRGB, spectrumHsv
 * ├── FanSpeed       → fanSpeed (low/medium/high/auto or numeric)
 * ├── OpenClose      → openPercent (0-100), isOpen
 * ├── TemperatureSetting → setTemperature (16-32°C), mode, thermostatMode
 * ├── TemperatureControl → temperature (ambient)
 * ├── HumiditySetting → humidity (0-100%)
 * ├── SensorState    → presence, motion, doorOpen, detected
 * ├── Illuminance    → luxValue
 * ├── EnergyStorage  → watt, voltage, current, wattHour
 * ├── AirQuality     → co2, tvoc, pm25
 * └── Toggles        → currentToggleSettings
 * 
 * ============================================================================
 * HOW TO ADD NEW TRAITS
 * ============================================================================
 * 
 * 1. Add new state properties to DeviceState interface (if needed)
 * 
 * 2. Create extractor function:
 *    ```typescript
 *    export const extractNewTrait = (data: any): ReturnType => {
 *      // Extract from various data formats
 *      const value = extractValue(data, 'keyName1', 'keyName2');
 *      return extractNumericValue(value, min, max); // with validation
 *    };
 *    ```
 * 
 * 3. Add to parseDeviceStateByTraits():
 *    ```typescript
 *    // NewTrait → newProperty
 *    if (hasTraits(['NewTrait'])) {
 *      const newValue = extractNewTrait(data);
 *      if (newValue !== undefined) {
 *        state.newProperty = newValue;
 *      }
 *    }
 *    ```
 * 
 * 4. Add to getDefaultTraitsForDeviceType() if there's a device type default
 * 
 * 5. Update isControllableByTraits() if it's a controllable trait
 * 
 * 6. Add unit tests in deviceStateParser.test.ts
 * 
 * 7. Update CLAUDE.md trait mapping table
 * 
 * ============================================================================
 */

import { logger } from './logger';

// Re-export IntelliSpaceDevice interface for external use
export interface DeviceState {
  // Power/Switch state
  isOn?: boolean;
  switchOn?: boolean;
  power?: boolean;
  
  // Power metering
  watt?: number;
  wattHour?: number;
  voltage?: number;
  current?: number;
  
  // Temperature & Humidity
  temperature?: number;
  humidity?: number;
  setTemperature?: number;
  
  // HVAC/Fan
  mode?: string;
  thermostatMode?: string;
  fanSpeed?: string;
  
  // Lighting
  brightness?: number;
  colorTemp?: number;
  colorTempMin?: number;
  colorTempMax?: number;
  
  // Sensors
  presence?: boolean;
  occupancy?: boolean;
  luxValue?: number;
  motionDetected?: boolean;
  isOpen?: boolean;
  
  // Curtain/Blind
  openPercent?: number;
  
  // Air quality
  co2?: number;
  tvoc?: number;
  pm25?: number;
  
  // Battery
  battery?: number;
  
  // Status
  online?: boolean;
  status?: 'online' | 'offline';
  lastUpdate?: string;
  
  // Custom attributes
  attributes?: Record<string, any>;
  currentToggleSettings?: Record<string, any>;
  toggles?: string[];  // Available toggle names
  toggleValues?: Record<string, boolean>;  // Current toggle values
}

/**
 * Helper to extract value from various data formats
 * Handles: direct value, { value: X }, nested objects, arrays
 */
export const extractValue = (obj: any, ...keys: string[]): any => {
  for (const key of keys) {
    const val = obj?.[key];
    if (val !== undefined && val !== null) {
      // Handle { value: X } format
      if (typeof val === 'object' && val.value !== undefined) {
        return Array.isArray(val.value) ? val.value[0] : val.value;
      }
      // Handle arrays - take first element
      if (Array.isArray(val)) {
        return val.length > 0 ? val[0] : undefined;
      }
      return val;
    }
  }
  return undefined;
};

/**
 * Helper to extract array value - returns first element or direct value
 */
export const extractArrayValue = (val: any, fallback?: any): any => {
  if (val === undefined || val === null) return fallback;
  if (Array.isArray(val)) return val.length > 0 ? val[0] : fallback;
  return val;
};

/**
 * Helper to extract numeric value with validation
 */
export const extractNumericValue = (val: any, min?: number, max?: number): number | undefined => {
  if (val === undefined || val === null) return undefined;
  const num = Number(Array.isArray(val) ? val[0] : val);
  if (isNaN(num) || !isFinite(num)) return undefined;
  if (min !== undefined && num < min) return undefined;
  if (max !== undefined && num > max) return undefined;
  return num;
};

// ============================================================================
// TRAIT EXTRACTORS
// ============================================================================

/**
 * Extract OnOff state from various data formats
 * Trait: OnOff → isOn, switchOn, power
 */
export const extractOnState = (data: any): boolean | undefined => {
  // Priority: direct 'on' field > state.on > iotState.on > values.on > currentModeSettings.on
  let onValue = extractValue(data, 'on', 'isOn', 'power', 'switchOn');
  
  if (onValue === undefined) {
    // Check nested state objects
    const stateObj = data?.state || data?.iotState;
    if (stateObj) {
      onValue = extractValue(stateObj, 'on', 'isOn', 'power');
      // Check currentModeSettings.on
      if (onValue === undefined && stateObj.currentModeSettings?.on !== undefined) {
        onValue = stateObj.currentModeSettings.on;
        if (typeof onValue === 'object' && onValue?.value !== undefined) {
          onValue = onValue.value;
        }
      }
    }
  }
  
  // Check values.on (API format)
  if (onValue === undefined && data?.values?.on !== undefined) {
    const onVal = data.values.on;
    onValue = onVal?.value !== undefined 
      ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
      : onVal;
  }
  
  if (onValue === undefined) return undefined;
  return onValue === true || onValue === 'true';
};

/**
 * Extract Brightness from various data formats
 * Trait: Brightness → brightness (0-100)
 */
export const extractBrightness = (data: any): number | undefined => {
  const brightness = extractValue(data, 'brightness');
  return extractNumericValue(brightness, 0, 100);
};

/**
 * Extract Color Settings from various data formats
 * Trait: ColorSetting → colorTemp, spectrumRGB, spectrumHsv
 */
export const extractColorSettings = (data: any): { 
  colorTemp?: number; 
  spectrumRGB?: number; 
  spectrumHsv?: { hue: number; saturation: number; value: number } 
} => {
  const result: { colorTemp?: number; spectrumRGB?: number; spectrumHsv?: any } = {};
  
  // Color temperature (2700-6500K typical range)
  const colorTemp = data?.color?.temperatureK ?? extractValue(data, 'colorTemp', 'colorTemperature');
  if (colorTemp !== undefined) {
    result.colorTemp = extractNumericValue(colorTemp, 2000, 10000);
  }
  
  // RGB color
  if (data?.color?.spectrumRGB !== undefined) {
    result.spectrumRGB = data.color.spectrumRGB;
  }
  
  // HSV color
  if (data?.color?.spectrumHsv !== undefined) {
    result.spectrumHsv = data.color.spectrumHsv;
  }
  
  return result;
};

/**
 * Extract Fan Speed from various data formats
 * Trait: FanSpeed → fanSpeed (low/medium/high/auto or numeric 0-3)
 */
export const extractFanSpeed = (data: any): string | undefined => {
  // Priority: currentFanSpeedSetting > fanSpeed > currentModeSettings.FanSpeed
  let fanSpeed = extractValue(data, 'currentFanSpeedSetting', 'fanSpeed', 'fan_speed');
  
  // Check currentModeSettings.FanSpeed (common in API responses)
  if (fanSpeed === undefined) {
    const modeSettings = data?.currentModeSettings ?? data?.values?.currentModeSettings?.value;
    if (modeSettings?.FanSpeed !== undefined) {
      fanSpeed = modeSettings.FanSpeed;
    }
  }
  
  if (fanSpeed === undefined) return undefined;
  return String(fanSpeed);
};

/**
 * Extract OpenClose state from various data formats
 * Trait: OpenClose → openPercent (0-100), isOpen
 */
export const extractOpenCloseState = (data: any): { openPercent?: number; isOpen?: boolean } => {
  const result: { openPercent?: number; isOpen?: boolean } = {};
  
  // openPercent (0-100, where 0 = closed, 100 = fully open)
  const openPercent = extractValue(data, 'openPercent');
  if (openPercent !== undefined) {
    result.openPercent = extractNumericValue(openPercent, 0, 100);
    if (result.openPercent !== undefined) {
      result.isOpen = result.openPercent > 0;
    }
  }
  
  // Check openState for open/close status
  const openState = extractValue(data, 'openState');
  if (openState !== undefined && result.isOpen === undefined) {
    result.isOpen = openState === 'OPEN' || openState === true;
  }
  
  return result;
};

/**
 * Extract Temperature Settings from various data formats
 * Trait: TemperatureSetting → setTemperature (16-32°C), mode
 */
export const extractTemperatureSettings = (data: any): { 
  setTemperature?: number; 
  mode?: string; 
  thermostatMode?: string 
} => {
  const result: { setTemperature?: number; mode?: string; thermostatMode?: string } = {};
  
  // Set temperature (thermostat setpoint)
  const setTemp = extractValue(data, 'thermostatTemperatureSetpoint', 'setTemperature', 'targetTemp');
  result.setTemperature = extractNumericValue(setTemp, 16, 32);
  
  // Thermostat mode
  const mode = extractValue(data, 'thermostatMode', 'mode');
  if (mode !== undefined) {
    result.mode = String(mode);
    result.thermostatMode = String(mode);
  }
  
  return result;
};

/**
 * Extract Temperature Control (ambient temperature) from various data formats
 * Trait: TemperatureControl → temperature
 */
export const extractTemperature = (data: any): number | undefined => {
  const temp = extractValue(data, 
    'thermostatTemperatureAmbient', 
    'temperatureAmbientCelsius',
    'temp', 
    'temperature', 
    'Temperature'
  );
  return extractNumericValue(temp, -50, 60);
};

/**
 * Extract Humidity from various data formats
 * Trait: HumiditySetting → humidity (0-100%)
 */
export const extractHumidity = (data: any): number | undefined => {
  const humidity = extractValue(data, 
    'humidityAmbientPercent',
    'thermostatHumidityAmbient',
    'rh', 
    'humidity', 
    'Humidity'
  );
  return extractNumericValue(humidity, 0, 100);
};

/**
 * Extract Sensor State (presence, motion, door state) from various data formats
 * Trait: SensorState → presence, occupancy, motionDetected, isOpen, detected
 */
export const extractSensorState = (data: any): {
  presence?: boolean;
  occupancy?: boolean;
  motionDetected?: boolean;
  isOpen?: boolean;
  detected?: string | boolean;
} => {
  const result: {
    presence?: boolean;
    occupancy?: boolean;
    motionDetected?: boolean;
    isOpen?: boolean;
    detected?: string | boolean;
  } = {};
  
  // Presence
  let presence = extractValue(data, 'presence', 'Presence');
  if (presence !== undefined) {
    result.presence = presence === true || presence === 'true';
  }
  
  // Occupancy
  let occupancy = extractValue(data, 'occupancy', 'Occupancy');
  if (occupancy !== undefined) {
    result.occupancy = occupancy === true || occupancy === 'true';
  }
  
  // Motion detection
  const detected = data?.detected?.value ?? data?.detected ?? 
                   data?.values?.detected?.value ?? 
                   data?.iotState?.detected;
  
  if (detected !== undefined) {
    if (Array.isArray(detected)) {
      // Check for motion
      if (detected.includes('motionDetected')) {
        result.motionDetected = true;
      }
      // Check for door state
      if (detected.includes('opened')) {
        result.isOpen = true;
      } else if (detected.includes('closed')) {
        result.isOpen = false;
      }
    } else if (typeof detected === 'boolean') {
      result.motionDetected = detected;
    } else if (typeof detected === 'string') {
      result.motionDetected = detected === 'motionDetected';
      result.isOpen = detected === 'opened';
    }
    result.detected = detected;
  }
  
  // Direct motion field
  if (result.motionDetected === undefined) {
    const motion = extractValue(data, 'motionDetected', 'motion');
    if (motion !== undefined) {
      result.motionDetected = motion === true;
    }
  }
  
  return result;
};

/**
 * Helper to extract lux value from various formats:
 * - Direct number: 155.99
 * - Array: [155.99]
 * - Object with value: { value: [155.99] } or { value: 155.99 }
 */
const extractLuxFromValue = (luxData: any): number | undefined => {
  if (luxData === undefined || luxData === null) return undefined;
  
  // Direct number
  if (typeof luxData === 'number') {
    return extractNumericValue(luxData, 0, 100000);
  }
  
  // Array: take last value
  if (Array.isArray(luxData) && luxData.length > 0) {
    return extractNumericValue(luxData[luxData.length - 1], 0, 100000);
  }
  
  // Object with value property (API format: lux = { value: [155.99] })
  if (typeof luxData === 'object' && luxData.value !== undefined) {
    return extractLuxFromValue(luxData.value); // Recursively extract from value
  }
  
  return undefined;
};

/**
 * Extract Illuminance (lux) from various data formats
 */
export const extractIlluminance = (data: any): number | undefined => {
  // Check multiple possible locations for lux data
  // Including endpoint "2" for Matter sensors that have lux on second endpoint
  const locations = [
    data?.lux,
    data?.illuminance,
    data?.iotState?.['2']?.lux,       // Matter sensors often have lux on endpoint 2
    data?.iotState?.['2']?.illuminance,
    data?.iotState?.['1']?.illuminance,
    data?.iotState?.['1']?.lux,
    data?.attributes?.['2']?.lux,
    data?.attributes?.['2']?.illuminance,
    data?.attributes?.['1']?.illuminance,
    data?.attributes?.['1']?.lux,
    data?.iotState?.events?.[0]?.data?.lux,
    data?.attributes?.['1']?.events?.[0]?.data?.lux,
  ];
  
  for (const loc of locations) {
    const val = extractLuxFromValue(loc);
    if (val !== undefined) return val;
  }
  
  return undefined;
};

/**
 * Extract Energy Storage (power metering) from various data formats
 * Trait: EnergyStorage → watt, voltage, current, wattHour
 */
export const extractEnergyData = (data: any): {
  watt?: number;
  wattHour?: number;
  voltage?: number;
  current?: number;
} => {
  const result: { watt?: number; wattHour?: number; voltage?: number; current?: number } = {};
  
  // Power (watt) - try real_power first (some meters use this), then watt
  const realPower = extractValue(data, 'real_power') ?? 
                    data?.state?.real_power ?? 
                    data?.iotState?.real_power;
  const wattPower = extractValue(data, 'watt', 'watts') ?? 
                    data?.state?.watt ?? 
                    data?.iotState?.watt;
  
  const watt = extractNumericValue(realPower ?? wattPower);
  if (watt !== undefined && watt >= 0) {
    result.watt = watt;
  }
  
  // Energy (watt-hour)
  const wattHour = extractValue(data, 'watt_hour', 'wattHour', 'energy', 'kwh');
  const wh = extractNumericValue(wattHour);
  if (wh !== undefined && wh >= 0) {
    result.wattHour = wh;
  }
  
  // Voltage
  const voltage = extractValue(data, 'voltage', 'volt', 'v');
  const v = extractNumericValue(voltage, 0, 500);
  if (v !== undefined) {
    result.voltage = v;
  }
  
  // Current
  const current = extractValue(data, 'current', 'amp', 'a');
  const a = extractNumericValue(current, 0, 1000);
  if (a !== undefined) {
    result.current = a;
  }
  
  return result;
};

/**
 * Extract Three-Phase power data
 */
export const extractThreePhaseData = (data: any): Record<string, number> | undefined => {
  const attrs: Record<string, number> = {};
  const stateObj = data?.state ?? data?.iotState ?? data;
  
  const keys = [
    'voltage_r', 'voltage_s', 'voltage_t',
    'current_r', 'current_s', 'current_t',
    'real_power_r', 'real_power_s', 'real_power_t',
    'watt_hour_r', 'watt_hour_s', 'watt_hour_t',
  ];
  
  for (const key of keys) {
    const val = extractValue(stateObj, key) ?? data?.[key];
    const num = extractNumericValue(val);
    if (num !== undefined) {
      attrs[key] = num;
    }
  }
  
  return Object.keys(attrs).length > 0 ? attrs : undefined;
};

/**
 * Extract Air Quality data
 */
export const extractAirQuality = (data: any): { co2?: number; tvoc?: number; pm25?: number } => {
  const result: { co2?: number; tvoc?: number; pm25?: number } = {};
  
  const co2 = extractValue(data, 'co2', 'CO2');
  if (co2 !== undefined) {
    result.co2 = extractNumericValue(co2, 0, 10000);
  }
  
  const tvoc = extractValue(data, 'tvoc', 'TVOC');
  if (tvoc !== undefined) {
    result.tvoc = extractNumericValue(tvoc, 0, 10000);
  }
  
  const pm25 = extractValue(data, 'pm25', 'PM25', 'pm2_5');
  if (pm25 !== undefined) {
    result.pm25 = extractNumericValue(pm25, 0, 1000);
  }
  
  return result;
};

/**
 * Extract Battery level
 */
export const extractBattery = (data: any): number | undefined => {
  const battery = extractValue(data, 'battery', 'batteryLevel', 'power');
  return extractNumericValue(battery, 0, 100);
};

/**
 * Extract Toggle Settings (for devices with custom toggles like Light, Buzzer)
 */
export const extractToggleSettings = (data: any): Record<string, any> | undefined => {
  let toggleSettings = data?.currentToggleSettings;
  
  if (toggleSettings && typeof toggleSettings === 'object' && toggleSettings.value !== undefined) {
    toggleSettings = toggleSettings.value;
  }
  
  if (toggleSettings === undefined && data?.values?.currentToggleSettings?.value !== undefined) {
    toggleSettings = data.values.currentToggleSettings.value;
  }
  
  return toggleSettings;
};

// ============================================================================
// MAIN PARSER FUNCTION
// ============================================================================

/**
 * Parse device state based on traits
 * This is the main function that replaces device-type-specific switch statements
 * 
 * @param data - Raw data from API or WebSocket
 * @param traits - Array of trait strings (e.g., ['OnOff', 'Brightness', 'ColorSetting'])
 * @param options - Additional options for parsing
 * @returns Partial device state object
 */
export const parseDeviceStateByTraits = (
  data: any,
  traits: string[] = [],
  options: {
    deviceType?: string;
    isWebSocket?: boolean;
    debug?: boolean;
  } = {}
): Partial<DeviceState> => {
  if (!data) return {};
  
  const state: Partial<DeviceState> = {};
  const { deviceType, debug } = options;
  
  // Normalize traits to handle various formats
  const normalizedTraits = traits.map(t => {
    // Handle trait variations like 'action.devices.traits.OnOff' → 'OnOff'
    const parts = t.split('.');
    return parts[parts.length - 1];
  });
  
  const hasTraits = (traitNames: string[]) => 
    traitNames.some(t => normalizedTraits.includes(t));
  
  // =========================================================================
  // TRAIT-BASED EXTRACTION
  // =========================================================================
  
  // OnOff trait → isOn, switchOn, power
  if (hasTraits(['OnOff'])) {
    const onState = extractOnState(data);
    if (onState !== undefined) {
      state.isOn = onState;
      state.switchOn = onState;
      state.power = onState;
    }
  }
  
  // Brightness trait → brightness
  if (hasTraits(['Brightness'])) {
    const brightness = extractBrightness(data);
    if (brightness !== undefined) {
      state.brightness = brightness;
    }
  }
  
  // ColorSetting trait → colorTemp, spectrumRGB, spectrumHsv
  if (hasTraits(['ColorSetting', 'ColorTemperature'])) {
    const colorSettings = extractColorSettings(data);
    if (colorSettings.colorTemp !== undefined) {
      state.colorTemp = colorSettings.colorTemp;
    }
    if (colorSettings.spectrumRGB !== undefined || colorSettings.spectrumHsv !== undefined) {
      state.attributes = {
        ...state.attributes,
        ...(colorSettings.spectrumRGB !== undefined && { spectrumRGB: colorSettings.spectrumRGB }),
        ...(colorSettings.spectrumHsv !== undefined && { spectrumHsv: colorSettings.spectrumHsv }),
      };
    }
  }
  
  // FanSpeed trait → fanSpeed
  if (hasTraits(['FanSpeed'])) {
    const fanSpeed = extractFanSpeed(data);
    if (fanSpeed !== undefined) {
      state.fanSpeed = fanSpeed;
    }
  }
  
  // OpenClose trait → openPercent, isOpen
  if (hasTraits(['OpenClose'])) {
    const openState = extractOpenCloseState(data);
    if (openState.openPercent !== undefined) {
      state.openPercent = openState.openPercent;
    }
    if (openState.isOpen !== undefined) {
      state.isOpen = openState.isOpen;
    }
  }
  
  // TemperatureSetting trait → setTemperature, mode
  if (hasTraits(['TemperatureSetting', 'ThermostatMode'])) {
    const tempSettings = extractTemperatureSettings(data);
    if (tempSettings.setTemperature !== undefined) {
      state.setTemperature = tempSettings.setTemperature;
    }
    if (tempSettings.mode !== undefined) {
      state.mode = tempSettings.mode;
    }
  }
  
  // TemperatureControl trait → temperature (ambient)
  if (hasTraits(['TemperatureControl', 'TemperatureSensor'])) {
    const temp = extractTemperature(data);
    if (temp !== undefined) {
      state.temperature = temp;
    }
  }
  
  // HumiditySetting trait → humidity
  if (hasTraits(['HumiditySetting', 'HumiditySensor'])) {
    const humidity = extractHumidity(data);
    if (humidity !== undefined) {
      state.humidity = humidity;
    }
  }
  
  // SensorState trait → presence, occupancy, motionDetected, isOpen
  if (hasTraits(['SensorState', 'OccupancySensing', 'Occupancy'])) {
    const sensorState = extractSensorState(data);
    Object.assign(state, sensorState);
  }
  
  // Illuminance trait → luxValue
  if (hasTraits(['Illuminance', 'LightSensor'])) {
    const lux = extractIlluminance(data);
    if (lux !== undefined) {
      state.luxValue = lux;
    }
  }
  
  // EnergyStorage trait → watt, wattHour, voltage, current
  if (hasTraits(['EnergyStorage', 'PowerMeter', 'EnergyMeter'])) {
    const energyData = extractEnergyData(data);
    Object.assign(state, energyData);
    
    // Three-phase data
    const threePhase = extractThreePhaseData(data);
    if (threePhase) {
      state.attributes = { ...state.attributes, ...threePhase };
      
      // Compute total watt if not already set
      if (state.watt === undefined) {
        const powerR = threePhase.real_power_r ?? 0;
        const powerS = threePhase.real_power_s ?? 0;
        const powerT = threePhase.real_power_t ?? 0;
        const totalWatt = powerR + powerS + powerT;
        if (!isNaN(totalWatt) && totalWatt >= 0) {
          state.watt = totalWatt;
        }
      }
    }
  }
  
  // Air Quality sensors
  if (hasTraits(['AirQuality', 'AirQualitySensor'])) {
    const airQuality = extractAirQuality(data);
    Object.assign(state, airQuality);
  }
  
  // Toggles trait → currentToggleSettings, toggles, toggleValues
  if (hasTraits(['Toggles'])) {
    const toggleSettings = extractToggleSettings(data);
    if (toggleSettings) {
      state.currentToggleSettings = toggleSettings;
      // Also set toggleValues for UI compatibility
      state.toggleValues = toggleSettings as Record<string, boolean>;
      // Extract available toggles from keys
      state.toggles = Object.keys(toggleSettings);
    }
    
    // Also check availableToggles in attributes
    const availableToggles = data?.attributes?.availableToggles || 
                             data?.availableToggles ||
                             data?.values?.availableToggles?.value;
    if (availableToggles && Array.isArray(availableToggles)) {
      state.toggles = availableToggles;
    }
  }
  
  // =========================================================================
  // COMMON FIELDS (extracted regardless of traits)
  // =========================================================================
  
  // Battery (many devices have battery, even without specific trait)
  const battery = extractBattery(data);
  if (battery !== undefined) {
    state.battery = battery;
  }
  
  // Online status
  if (data?.online !== undefined) {
    state.online = data.online;
    state.status = data.online ? 'online' : 'offline';
  }
  
  if (debug) {
    logger.log(`🔧 [DeviceStateParser] Parsed state for ${deviceType || 'unknown'}:`, {
      traits: normalizedTraits,
      parsedState: state,
      rawData: data,
    });
  }
  
  return state;
};

/**
 * Get default traits based on device type
 * This provides fallback traits when device doesn't have explicit trait info
 */
export const getDefaultTraitsForDeviceType = (deviceType: string): string[] => {
  switch (deviceType) {
    case 'power_meter':
      return ['EnergyStorage', 'OnOff'];
    case 'power_meter_3phase':
      return ['EnergyStorage'];
    case 'th_sensor':
      return ['TemperatureControl', 'HumiditySetting'];
    case 'presence_sensor':
      return ['OccupancySensing', 'Illuminance'];
    case 'lux_sensor':
      return ['Illuminance'];
    case 'air_quality_sensor':
      return ['TemperatureControl', 'HumiditySetting', 'AirQuality'];
    case 'hvac':
      return ['OnOff', 'TemperatureSetting', 'FanSpeed', 'TemperatureControl', 'HumiditySetting', 'Toggles'];
    case 'dehumidifier':
      return ['OnOff', 'HumiditySetting', 'FanSpeed', 'Toggles'];
    case 'fan':
      return ['OnOff', 'FanSpeed', 'Toggles'];
    case 'light':
      return ['OnOff', 'Brightness', 'ColorSetting'];
    case 'switch':
      return ['OnOff', 'EnergyStorage'];
    case 'outlet':
      return ['OnOff', 'EnergyStorage'];
    case 'door_sensor':
      return ['SensorState'];
    case 'motion_sensor':
      return ['SensorState'];
    case 'curtain':
    case 'blind':
      return ['OpenClose'];
    case 'button':
      return [];
    default:
      return [];
  }
};

/**
 * Check if device is controllable based on traits
 */
export const isControllableByTraits = (traits: string[]): boolean => {
  const controllableTraits = [
    'OnOff', 'Brightness', 'ColorSetting', 'FanSpeed', 'OpenClose',
    'TemperatureSetting', 'Volume', 'Modes', 'Toggles', 'LockUnlock',
    'StartStop', 'Timer'
  ];
  
  const normalizedTraits = traits.map(t => {
    const parts = t.split('.');
    return parts[parts.length - 1];
  });
  
  return normalizedTraits.some(t => controllableTraits.includes(t));
};

/**
 * Get state description based on device state and traits
 * Returns human-readable state text (e.g., "ON", "OFF", "75%", "26°C")
 */
export const getStateDescriptionByTraits = (
  state: Partial<DeviceState>,
  traits: string[],
  deviceType?: string
): string => {
  const normalizedTraits = traits.map(t => {
    const parts = t.split('.');
    return parts[parts.length - 1];
  });
  
  const hasTraits = (traitNames: string[]) => 
    traitNames.some(t => normalizedTraits.includes(t));
  
  const isOn = state.isOn || state.switchOn || state.power;
  
  // Light with brightness and color temp
  if (hasTraits(['Brightness']) && deviceType === 'light') {
    if (!isOn) return 'OFF';
    const brightness = state.brightness !== undefined ? `${state.brightness}%` : '';
    const colorTemp = state.colorTemp !== undefined ? `${state.colorTemp}K` : '';
    if (brightness && colorTemp) return `${brightness} ${colorTemp}`;
    return brightness || 'ON';
  }
  
  // Curtain/Blind with openPercent
  if (hasTraits(['OpenClose'])) {
    if (state.openPercent !== undefined) {
      if (state.openPercent === 0) return '全關';
      if (state.openPercent === 100) return '全開';
      return `${state.openPercent}%`;
    }
    return state.isOpen ? '開啟' : '關閉';
  }
  
  // HVAC with temperature
  if (hasTraits(['TemperatureSetting']) && deviceType === 'hvac') {
    return isOn ? 'ON' : 'OFF';
  }
  
  // Fan (special case - Chinese handled in UI)
  if (deviceType === 'fan') {
    return 'OFF';
  }
  
  // Default OnOff
  if (hasTraits(['OnOff'])) {
    return isOn ? 'ON' : 'OFF';
  }
  
  return 'OFF';
};

/**
 * Get state text color based on device state and traits
 */
export const getStateTextColorByTraits = (
  state: Partial<DeviceState>,
  traits: string[],
  deviceType?: string
): string => {
  const normalizedTraits = traits.map(t => {
    const parts = t.split('.');
    return parts[parts.length - 1];
  });
  
  const hasTraits = (traitNames: string[]) => 
    traitNames.some(t => normalizedTraits.includes(t));
  
  const isOn = state.isOn || state.switchOn || state.power;
  
  // Device-type specific colors
  if (deviceType === 'fan') {
    return isOn ? 'text-cyan-500' : 'text-gray-400';
  }
  
  if (deviceType === 'light') {
    return isOn ? 'text-orange-500' : 'text-gray-400';
  }
  
  if (hasTraits(['OpenClose'])) {
    const curtainIsOpen = state.openPercent !== undefined ? state.openPercent > 0 : state.isOpen;
    if (deviceType === 'blind') {
      return curtainIsOpen ? 'text-cyan-500' : 'text-gray-400';
    }
    return curtainIsOpen ? 'text-teal-500' : 'text-gray-400';
  }
  
  // Default for OnOff devices
  if (hasTraits(['OnOff'])) {
    return isOn ? 'text-gray-900 dark:text-gray-900' : 'text-gray-400';
  }
  
  return 'text-gray-400';
};

